import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Save, X, KeyRound, Copy } from "lucide-react";
import api, { apiErrorMsg } from "@/lib/api";

export default function PengasuhPage() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nama: "", password: "" });
  const [creating, setCreating] = useState(false);
  const [resetting, setResetting] = useState(null); // pengasuh id -> new password
  const [pwModal, setPwModal] = useState(null); // {nama, password}

  const load = () => {
    setLoading(true);
    api.get("/admin/pengasuh").then((r) => setList(r.data)).catch((e) => toast.error(apiErrorMsg(e))).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const submitNew = async (e) => {
    e.preventDefault();
    if (!form.nama) return toast.error("Nama wajib diisi");
    try {
      const { data } = await api.post("/admin/pengasuh", form);
      const pwd = data.default_password || form.password;
      setPwModal({ nama: form.nama, password: pwd });
      setForm({ nama: "", password: "" });
      setCreating(false);
      load();
    } catch (e) { toast.error(apiErrorMsg(e)); }
  };

  const saveEdit = async (id) => {
    const payload = {};
    if (editing.nama !== editing._original_nama) payload.nama = editing.nama;
    if (editing.password) payload.password = editing.password;
    if (Object.keys(payload).length === 0) {
      setEditing(null);
      return;
    }
    try {
      await api.put(`/admin/pengasuh/${id}`, payload);
      toast.success("Tersimpan");
      setEditing(null);
      load();
    } catch (e) { toast.error(apiErrorMsg(e)); }
  };

  const resetPwd = async (p) => {
    if (!window.confirm(`Reset sandi ${p.nama}?`)) return;
    try {
      const { data } = await api.post(`/admin/pengasuh/${p.id}/reset-password`);
      setPwModal({ nama: p.nama, password: data.default_password });
    } catch (e) { toast.error(apiErrorMsg(e)); }
  };

  const remove = async (id, nama) => {
    if (!window.confirm(`Hapus pengasuh ${nama}?`)) return;
    try {
      await api.delete(`/admin/pengasuh/${id}`);
      toast.success("Dihapus");
      load();
    } catch (e) { toast.error(apiErrorMsg(e)); }
  };

  return (
    <div className="p-4 md:p-8">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-brass-600 font-semibold mb-1">Master Data</div>
          <h1 className="font-heading text-3xl font-bold text-navy-900">Data Pengasuh</h1>
          <p className="text-slate-500 mt-1 text-sm">Pembina yang mengisi observasi & ranking.</p>
        </div>
        <button onClick={() => setCreating(!creating)} data-testid="pengasuh-add-btn" className="bg-brass-500 hover:bg-brass-600 text-white px-4 py-2 rounded-sm font-semibold text-sm flex items-center gap-2">
          <Plus className="w-4 h-4" /> Tambah Pengasuh
        </button>
      </div>

      {creating && (
        <form onSubmit={submitNew} className="bg-white border border-brass-300 rounded-sm p-4 mb-4 grid grid-cols-1 md:grid-cols-3 gap-3 items-end animate-fade-in">
          <div>
            <label className="block text-xs uppercase font-semibold text-slate-500 mb-1">Nama Pengasuh</label>
            <input autoFocus value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} placeholder="Nama lengkap" data-testid="pengasuh-input-nama" className="w-full px-3 py-2 border border-slate-300 rounded-sm focus:ring-2 focus:ring-brass-500 focus:border-brass-500 outline-none text-sm" />
          </div>
          <div>
            <label className="block text-xs uppercase font-semibold text-slate-500 mb-1">Sandi (opsional)</label>
            <input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Kosongkan → pengasuh123" className="w-full px-3 py-2 border border-slate-300 rounded-sm focus:ring-2 focus:ring-brass-500 focus:border-brass-500 outline-none text-sm font-mono" />
          </div>
          <div className="flex gap-2">
            <button type="submit" data-testid="pengasuh-save-btn" className="bg-navy-900 hover:bg-navy-800 text-white px-4 py-2 rounded-sm text-sm flex items-center gap-2"><Save className="w-4 h-4" /> Simpan</button>
            <button type="button" onClick={() => setCreating(false)} className="px-4 py-2 rounded-sm border border-slate-300 text-sm hover:bg-slate-50">Batal</button>
          </div>
        </form>
      )}

      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-100 text-slate-700 text-xs uppercase tracking-wide">
              <th className="text-left p-3 w-12">#</th>
              <th className="text-left p-3">Nama</th>
              <th className="text-left p-3 w-48">Sandi Baru (opsional)</th>
              <th className="text-right p-3 w-48">Aksi</th>
            </tr>
          </thead>
          <tbody data-testid="pengasuh-list">
            {loading ? (
              <tr><td colSpan="4" className="p-4 md:p-8 text-center text-slate-400">Memuat...</td></tr>
            ) : list.length === 0 ? (
              <tr><td colSpan="4" className="p-4 md:p-8 text-center text-slate-400">Belum ada pengasuh.</td></tr>
            ) : list.map((p, i) => (
              <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="p-3 text-slate-500 font-mono">{i + 1}</td>
                <td className="p-3">
                  {editing?.id === p.id ? (
                    <input value={editing.nama} onChange={(e) => setEditing({ ...editing, nama: e.target.value })} className="w-full px-2 py-1 border border-slate-300 rounded-sm text-sm" />
                  ) : <span className="font-medium text-navy-900">{p.nama}</span>}
                </td>
                <td className="p-3">
                  {editing?.id === p.id ? (
                    <input type="text" value={editing.password || ""} onChange={(e) => setEditing({ ...editing, password: e.target.value })} placeholder="Kosongkan jika tidak diganti" className="w-full px-2 py-1 border border-slate-300 rounded-sm text-sm font-mono" />
                  ) : <span className="text-slate-400 text-xs">••••••••</span>}
                </td>
                <td className="p-3 text-right">
                  {editing?.id === p.id ? (
                    <div className="flex justify-end gap-1">
                      <button onClick={() => saveEdit(p.id)} className="p-1.5 text-emerald-700 hover:bg-emerald-50 rounded-sm"><Save className="w-4 h-4" /></button>
                      <button onClick={() => setEditing(null)} className="p-1.5 text-slate-500 hover:bg-slate-100 rounded-sm"><X className="w-4 h-4" /></button>
                    </div>
                  ) : (
                    <div className="flex justify-end gap-1">
                      <button onClick={() => setEditing({ ...p, _original_nama: p.nama, password: "" })} className="p-1.5 text-blue-700 hover:bg-blue-50 rounded-sm" title="Edit"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => resetPwd(p)} className="p-1.5 text-amber-700 hover:bg-amber-50 rounded-sm" title="Reset Sandi"><KeyRound className="w-4 h-4" /></button>
                      <button onClick={() => remove(p.id, p.nama)} className="p-1.5 text-red-700 hover:bg-red-50 rounded-sm" title="Hapus"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-4 text-xs text-slate-500">Total: <span className="font-bold text-navy-900 font-mono">{list.length}</span> pengasuh</div>

      {/* Password reveal modal */}
      {pwModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setPwModal(null)}>
          <div className="bg-white rounded-sm p-6 max-w-md w-full border-t-4 border-brass-500" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading font-bold text-lg text-navy-900 mb-3">Sandi untuk {pwModal.nama}</h3>
            <p className="text-sm text-slate-600 mb-4">Catat dan berikan sandi ini kepada pengasuh. Setelah ditutup, sandi tidak bisa ditampilkan ulang.</p>
            <div className="bg-slate-100 border border-slate-300 rounded-sm p-3 flex items-center justify-between mb-4">
              <code className="font-mono font-bold text-navy-900">{pwModal.password}</code>
              <button onClick={() => { navigator.clipboard.writeText(pwModal.password); toast.success("Disalin"); }} className="text-slate-600 hover:text-navy-900"><Copy className="w-4 h-4" /></button>
            </div>
            <button onClick={() => setPwModal(null)} className="w-full bg-navy-900 hover:bg-navy-800 text-white py-2 rounded-sm text-sm font-semibold">Tutup</button>
          </div>
        </div>
      )}
    </div>
  );
}
